/**
 * VendorPortalOrderService
 * Service for vendor order management in the portal.
 * Allows vendors to view orders containing their products.
 * Route: api/vendor-portal/orders, api/vendor-portal/order-lines, api/vendor-portal/order-line-addons
 */
import { VendorPortalBaseService } from './VendorPortalBaseService';
/**
 * VendorPortalOrderService
 * Handles vendor order operations for the vendor portal.
 * Backend now returns enriched ModelView types instead of raw entities.
 * Maps to /api/vendor-portal/orders, /api/vendor-portal/order-lines,
 * and /api/vendor-portal/order-line-addons endpoints
 */
export class VendorPortalOrderService extends VendorPortalBaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? VendorPortalOrderService.DEFAULT_SERVICE_PATH,
            basePath: config?.basePath ?? VendorPortalOrderService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // ==================== ORDERS ====================
    /**
     * Get all orders containing the vendor's products.
     * Applies client-side vendor line filtering as defense-in-depth.
     * GET /api/vendor-portal/orders
     */
    async getOrders() {
        const response = await this.getOrdersRaw();
        const orders = response.data ?? [];
        return orders.map(order => this.filterOrderLinesByVendor(order));
    }
    /**
     * Get all vendor orders (raw response)
     */
    getOrdersRaw() {
        return this.get('orders');
    }
    /**
     * Get a specific order by ID with vendor's lines.
     * Applies client-side vendor line filtering as defense-in-depth.
     * GET /api/vendor-portal/orders/{id}
     */
    async getOrderById(id) {
        const response = await this.getOrderByIdRaw(id);
        if (!response.data) {
            throw new Error(`Order ${id} not found`);
        }
        return this.filterOrderLinesByVendor(response.data);
    }
    /**
     * Get order by ID (raw response)
     */
    getOrderByIdRaw(id) {
        return this.get(`orders/${id}`);
    }
    /**
     * Get vendor order summary (counts, totals)
     * GET /api/vendor-portal/orders/summary
     */
    async getOrderSummary() {
        const response = await this.getOrderSummaryRaw();
        if (!response.data) {
            throw new Error('Failed to retrieve order summary');
        }
        return response.data;
    }
    /**
     * Get order summary (raw response)
     */
    getOrderSummaryRaw() {
        return this.get('orders/summary');
    }
    // ==================== ORDER LINES ====================
    /**
     * Get all order lines belonging to the vendor
     * GET /api/vendor-portal/order-lines
     */
    async getOrderLines() {
        const response = await this.getOrderLinesRaw();
        return response.data ?? [];
    }
    /**
     * Get all vendor order lines (raw response)
     */
    getOrderLinesRaw() {
        return this.get('order-lines');
    }
    /**
     * Get a specific order line by ID
     * GET /api/vendor-portal/order-lines/{id}
     */
    async getOrderLineById(id) {
        const response = await this.getOrderLineByIdRaw(id);
        if (!response.data) {
            throw new Error(`Order line ${id} not found`);
        }
        return response.data;
    }
    /**
     * Get order line by ID (raw response)
     */
    getOrderLineByIdRaw(id) {
        return this.get(`order-lines/${id}`);
    }
    /**
     * Get order lines for a specific order (vendor's lines only)
     * GET /api/vendor-portal/order-lines/by-order/{orderId}
     */
    async getOrderLinesByOrder(orderId) {
        const response = await this.getOrderLinesByOrderRaw(orderId);
        return response.data ?? [];
    }
    /**
     * Get order lines by order (raw response)
     */
    getOrderLinesByOrderRaw(orderId) {
        return this.get(`order-lines/by-order/${orderId}`);
    }
    // ==================== ORDER LINE STATUS ====================
    /**
     * Update an order line's fulfilment status
     * PATCH /api/vendor-portal/order-lines/{lineId}/status
     */
    async updateOrderLineStatus(lineId, statusID) {
        const response = await this.updateOrderLineStatusRaw(lineId, statusID);
        if (!response.data) {
            throw new Error(`Failed to update order line ${lineId} status`);
        }
        return response.data;
    }
    /**
     * Update order line status (raw response)
     */
    updateOrderLineStatusRaw(lineId, statusID) {
        return this.patch(`order-lines/${lineId}/status`, { statusID });
    }
    // ==================== ORDER LINE ADD-ONS ====================
    /**
     * Get all order line add-ons for the vendor's lines
     * GET /api/vendor-portal/order-line-addons
     */
    async getOrderLineAddOns() {
        const response = await this.getOrderLineAddOnsRaw();
        return response.data ?? [];
    }
    /**
     * Get all vendor order line add-ons (raw response)
     */
    getOrderLineAddOnsRaw() {
        return this.get('order-line-addons');
    }
    /**
     * Get a specific order line add-on by ID
     * GET /api/vendor-portal/order-line-addons/{id}
     */
    async getOrderLineAddOnById(id) {
        const response = await this.getOrderLineAddOnByIdRaw(id);
        if (!response.data) {
            throw new Error(`Order line add-on ${id} not found`);
        }
        return response.data;
    }
    /**
     * Get order line add-on by ID (raw response)
     */
    getOrderLineAddOnByIdRaw(id) {
        return this.get(`order-line-addons/${id}`);
    }
    /**
     * Get add-ons for a specific order line
     * GET /api/vendor-portal/order-line-addons/by-line/{lineId}
     */
    async getOrderLineAddOnsByLine(lineId) {
        const response = await this.getOrderLineAddOnsByLineRaw(lineId);
        return response.data ?? [];
    }
    /**
     * Get add-ons by line (raw response)
     */
    getOrderLineAddOnsByLineRaw(lineId) {
        return this.get(`order-line-addons/by-line/${lineId}`);
    }
    // ==================== ORDER LINE VARIANTS ====================
    /**
     * Get all order line variants for the vendor's lines
     * GET /api/vendor-portal/order-line-variants
     */
    async getOrderLineVariants() {
        const response = await this.getOrderLineVariantsRaw();
        return response.data ?? [];
    }
    /**
     * Get all vendor order line variants (raw response)
     */
    getOrderLineVariantsRaw() {
        return this.get('order-line-variants');
    }
    /**
     * Get a specific order line variant by ID
     * GET /api/vendor-portal/order-line-variants/{id}
     */
    async getOrderLineVariantById(id) {
        const response = await this.getOrderLineVariantByIdRaw(id);
        if (!response.data) {
            throw new Error(`Order line variant ${id} not found`);
        }
        return response.data;
    }
    /**
     * Get order line variant by ID (raw response)
     */
    getOrderLineVariantByIdRaw(id) {
        return this.get(`order-line-variants/${id}`);
    }
    /**
     * Get variants for a specific order line
     * GET /api/vendor-portal/order-line-variants/by-line/{lineId}
     */
    async getOrderLineVariantsByLine(lineId) {
        const response = await this.getOrderLineVariantsByLineRaw(lineId);
        return response.data ?? [];
    }
    /**
     * Get variants by line (raw response)
     */
    getOrderLineVariantsByLineRaw(lineId) {
        return this.get(`order-line-variants/by-line/${lineId}`);
    }
    // ==================== PRIVATE HELPERS ====================
    /**
     * Filter an order's lines to only include the current vendor's items.
     * Defense-in-depth: the backend should already filter, but this ensures
     * vendors never see other vendors' line items even if the API response is unfiltered.
     */
    filterOrderLinesByVendor(order) {
        const vendorId = this.getVendorId();
        if (!vendorId || !order.lines?.length)
            return order;
        const filteredLines = order.lines.filter((line) => line.vendorID === vendorId);
        // If all lines already belong to this vendor, return as-is
        if (filteredLines.length === order.lines.length)
            return order;
        return {
            ...order,
            lines: filteredLines,
            lineCount: filteredLines.length,
            itemCount: filteredLines.reduce((sum, l) => sum + l.quantity, 0),
            linesTotal: filteredLines.reduce((sum, l) => sum + l.amount, 0),
        };
    }
}
VendorPortalOrderService.DEFAULT_SERVICE_PATH = 'orders';
VendorPortalOrderService.BASE_PATH = 'api/vendor-portal';
//# sourceMappingURL=VendorPortalOrderService.js.map